import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router";
import CookieBanner from "react-cookie-banner";
import { createBrowserHistory } from "history";

import { useRootStore } from "store";
import { getTabToken, getWorkingTabCustomer } from "common/MetaActions";
import Layout from "views/layout/containers/Default";
import LoginLayout from "views/layout/containers/Login";
import ErrorNotifications from "views/errorNotifications/containers/ErrorNotifications";
import { captureEvent } from "components/posthog/customEventCapture";
import PageViewTracker from "components/posthog/PageViewTracker";
import {
  EditingState,
  GlobalContextProvider,
  RedirectingState,
  NavigationOptions
} from "./store/GlobalContext";
import { ToasterNotification } from "components/toastNotification/ToasterNotification";

import "styles/main.scss";
import "./App.scss";

export default function App() {
  const navigate = useNavigate();

  const { setWorkingCustomerId, setStaticInformation } = useRootStore(
    (state) => state.global
  );

  const { token, isAuthenticated, loginUser } = useRootStore(
    (state) => state.user
  );
  const [editingState, setEditingState] = useState<EditingState>({
    isEditing: false,
    location: null
  });

  const [redirectingState, setRedirectingState] = useState<RedirectingState>({
    url: "",
    isRedirecting: false,
    clickEvent: undefined,
    shouldHardRedirect: false
  });

  const handleNavigation = ({
    url,
    eventTitle,
    e,
    clickEvent,
    shouldHardRedirect = false
  }: NavigationOptions) => {
    if (eventTitle) {
      captureEvent(eventTitle);
    }
    if (editingState.isEditing) {
      e?.preventDefault();
      setRedirectingState({
        url,
        isRedirecting: true,
        clickEvent,
        shouldHardRedirect
      });
    } else {
      if (clickEvent) {
        clickEvent();
      }

      setEditingState({ isEditing: false, location: null });
      setRedirectingState({
        url: "",
        isRedirecting: false,
        clickEvent: undefined,
        shouldHardRedirect: false
      });

      if (shouldHardRedirect && typeof url === "string") {
        window.location.href = url;
      } else {
        if (typeof url === "number") {
          navigate(url); // navigate(-1)
        } else {
          navigate(url); // navigate("/path")
        }
      }
    }
  };

  if (getTabToken() && !token) {
    loginUser(getTabToken());
    const workingTabCustomer = getWorkingTabCustomer();
    if (workingTabCustomer) {
      setWorkingCustomerId(workingTabCustomer);
    }
  }

  useEffect(() => {
    setStaticInformation();
    const cookieConsent = document.cookie
      .split("; ")
      .find((row) => row.startsWith("user-has-accepted-cookies"));
    if (cookieConsent && cookieConsent.split("=")[1] === "true") {
      posthogInit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const posthogInit = async () => {
    if (process.env.REACT_APP_STAGE !== "production") {
      return;
    }

    const posthogApiKey = process.env.REACT_APP_POSTHOG_API_KEY;
    const posthogApiHost = process.env.REACT_APP_POSTHOG_API_HOST;
    if (posthogApiKey && posthogApiHost) {
      const posthog = await import("posthog-js");
      posthog.default.init(posthogApiKey, {
        api_host: posthogApiHost,
        autocapture: false,
        rageclick: false,
        capture_pageview: "history_change",
        capture_pageleave: false,
        disable_session_recording: true,
        disable_surveys: true,
        disable_web_experiments: true,
        ip: false,
        capture_dead_clicks: false
      });
    }
  };

  const link = (
    <a href="https://www.ludvig.se/cookies/" target="_blank" rel="noreferrer">
      Läs mer
    </a>
  );
  const history = createBrowserHistory();

  return (
    <GlobalContextProvider
      editingState={editingState}
      redirectingState={redirectingState}
      setEditingState={setEditingState}
      setRedirectingState={setRedirectingState}
      handleNavigation={handleNavigation}
    >
      <PageViewTracker history={history} />

      <CookieBanner
        message="Vi tar hjälp av cookies för att tillhandahålla våra tjänster. Genom att använda våra tjänster godkänner du att vi använder cookies. "
        link={link}
        buttonMessage="x"
        className="cookieBanner"
        disableStyle={true}
        onAccept={posthogInit}
        cookie="user-has-accepted-cookies"
        dismissOnScroll={false}
      />
      <Routes>
        <Route
          path="/*"
          element={isAuthenticated ? <Layout /> : <LoginLayout />}
        />
      </Routes>
      <ErrorNotifications />
      <ToasterNotification />
    </GlobalContextProvider>
  );
}
